/**
 * [BaselineItems] Step 3 of the Baseline Management workflow (Item management).
 * - Orchestrates the UI flow between the BaselineItemList (table view) and BaselineItemConfigs (detail/edit view).
 * - Uses local state to track the currently selected item and item list, always sourced from Redux and deep-cloned for local edits.
 * - Handles row/item selection, add/edit, and status changes for items within a baseline.
 * - All asynchronous data fetch and prep logic is encapsulated in the useBaselineItemsData custom hook.
 * - Follows the RAVEN design pattern:
 *    - No direct Redux mutation; local clones only.
 *    - All CRUD/status/selection handlers call up to parent via props.
 *    - UI is orchestrator/stepper only; all business/data logic is delegated to hooks/utilities.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.item - The current baseline record from Redux.
 * @param {string} props.mode - Controls editability of the view ("edit", "view", etc.).
 * @param {string} [props.size] - UI size for text fields/selects.
 * @param {function(Object):void} props.onSetCurrentBaseline - Callback to bubble up baseline changes.
 * @param {function():void} props.onClearComplete - Callback to clear/reset UI state after save.
 * @param {function(number, function):void} props.onSaveDataToDB - Callback to trigger DB save for baseline/items.
 * @param {function(boolean):void} props.onSetDataChanged - Callback to notify parent if item data is modified.
 * @param {function():void} props.onReloadBaseline - Callback to trigger reloading of current baseline from DB.
 * @returns {React.ReactElement}
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

// Custom hooks
import { useBaselinesData } from "src/hooks/useBaselinesData";
import { useBaselineItemsData } from "src/hooks/useBaselineItemsData";
import { useLookupsData } from "src/context/LookupsContext";
import { useBaselineItemActions } from "src/hooks/useBaselineItemActions";

// Layout/UI
import {
	Box,
	Button,
	ButtonGroup,
	Checkbox,
	createListCollection,
	IconButton,
	MetreaSelectContent,
	MetreaSelectItem,
	MetreaSelectRoot,
	MetreaSelectTrigger,
	MetreaSelectValueText,
	Pagination,
	Portal,
	Spinner,
} from "@metrea-ui/v2";
import { ChevronLeftRegular, ChevronRightRegular } from "@fluentui/react-icons";
import BaselineItemList from "./BaselineItemList";
import BaselineItemConfigs from "./BaselineItemConfigs";
import { InputFileUpload } from "src/components/UI/Upload/InputFileUpload";
import { AppModal } from "src/components/UI/common/AppModal";

// Utilities
import { Session, isAdmin, isDeveloper, isDevTeam } from "src/utils/sessionUtils";
import { debugLog } from "src/utils/debugUtils";
import { deepClone } from "src/utils/dataUtils";
import BaselineService from "src/services/BaselineService";
import { useDialogBox } from "src/hooks/useDialogBox";

// Constants
import { initialBaselineItemState } from "src/constants/initialStates";

const reportListStyle = {
	margin: "0.35rem 0 0",
	paddingLeft: "1.2rem",
	fontSize: "0.85rem",
};

// Mirror the compact RecordNav add-button sizing locally for the Step 3 upload
// action without importing RecordNav's CSS module into this test-covered path.
const uploadButtonStyle = {
	minHeight: "26px",
	padding: "2px 10px",
	fontSize: "0.75rem",
	lineHeight: "1rem",
};

const reviewEntryTextStyle = {
	fontSize: "0.85rem",
};

const ingestRuntimeStatusBoxStyle = {
	padding: "10px 12px",
	border: "1px solid rgba(0, 0, 0, 0.12)",
	borderRadius: "6px",
	background: "rgba(0, 0, 0, 0.03)",
	fontSize: "0.85rem",
	lineHeight: "1.25rem",
	width: "100%",
};

const developerOnlyPanelStyle = {
	marginTop: "0.75rem",
	padding: "0.75rem 0.9rem",
	border: "1px dashed rgba(44, 82, 130, 0.45)",
	borderRadius: "6px",
	background: "rgba(66, 153, 225, 0.08)",
	display: "grid",
	gap: "0.35rem",
};

const developerOnlyBadgeStyle = {
	display: "inline-flex",
	alignItems: "center",
	width: "fit-content",
	padding: "2px 8px",
	borderRadius: "999px",
	background: "rgba(44, 82, 130, 0.14)",
	color: "rgb(44, 82, 130)",
	fontSize: "0.75rem",
	fontWeight: 600,
	letterSpacing: "0.01em",
	textTransform: "uppercase",
};

const reviewRowsPerPageOptions = [5, 10, 20, 50];
const BASELINE_ITEM_REVIEW_PDF_MAX_BYTES = 1024 * 1024 * 50;
const BASELINE_ITEM_IMPORT_JSON_MAX_BYTES = 1024 * 1024 * 20;
const BASELINE_ITEM_JSON_ONLY_COPY = "JSON upload is available now. PDF ingest review is coming soon.";

/**
 * 1. show JSON upload only, with PDF coming soon
 * 2. show generated JSON analysis/review only
 *    - no import button
 *    - no row selection / checkboxes
 *    - no mapping assistance
 * 3. show generated JSON analysis/review plus one import action for the reviewed rows
 *    - allow import of the reviewed rows as a whole
 *    - still no row selection / checkboxes
 *    - still no mapping assistance
 * 4. show row selection / checkboxes
 * 5. show "Use mapping assistance"
 * 6. allow PDF upload / PDF ingest review
 */
const buildBaselineItemIngestRolloutCapabilities = () => {
	// Admins follow the current staged rollout lane, while dev-team testing can
	// temporarily unlock the full flow to keep profile-pack development moving.
	const adminRolloutStage = 2;
	const rolloutStage = isDevTeam() ? 6 : isAdmin() ? adminRolloutStage : 1;

	return {
		canUploadJson: true,
		canReviewGeneratedJson: rolloutStage >= 2,
		canImportReviewedRows: rolloutStage >= 3,
		canSelectReviewedRows: rolloutStage >= 4,
		canUseMappingAssistance: rolloutStage >= 5,
		canUploadPdf: rolloutStage >= 6,
		isReadOnlyReview: rolloutStage === 2,
	};
};

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const formatMegabyteLimitLabel = (maxBytes) => `${Math.floor(maxBytes / (1024 * 1024))} MB`;

const formatElapsedTimerLabel = (elapsedMs) => {
	const safeElapsedMs = Number.isFinite(elapsedMs) && elapsedMs > 0 ? elapsedMs : 0;
	const totalSeconds = Math.floor(safeElapsedMs / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${String(minutes).padStart(2, "0")}'${String(seconds).padStart(2, "0")}"`;
};

const getPendingRoutingLaneLabel = (intakeRouting, reviewIsStillLoading) => {
	if (typeof intakeRouting?.matchKind === "string" && intakeRouting.matchKind.trim()) {
		return intakeRouting.matchKind.trim();
	}
	return reviewIsStillLoading ? "pending authoritative route" : "(unknown)";
};

const getSourceProfileDisplayLabel = (profile) => {
	if (typeof profile?.displayLabel === "string" && profile.displayLabel.trim()) {
		return profile.displayLabel.trim();
	}
	if (typeof profile?.label === "string" && profile.label.trim()) {
		return profile.label.trim();
	}
	return String(profile?.id || "").trim();
};

const getPdfRuntimeStatusLabel = (pdfRuntime) => {
	const status = String(pdfRuntime?.provider?.status || "").trim().toLowerCase();
	if (status === "configured") return "PDF provider configured";
	if (status === "invalid_config") return "PDF provider misconfigured";
	if (status === "unconfigured") return "PDF provider not configured";
	return "PDF provider status unknown";
};

const formatPdfRuntimeSupportedLanes = (pdfRuntime) => {
	const provenLanes = Array.isArray(pdfRuntime?.provenStage3ServiceRouteIds)
		? pdfRuntime.provenStage3ServiceRouteIds
		: [];
	return provenLanes.length > 0 ? provenLanes.join(", ") : "none";
};

const getPdfRuntimeConfigHint = (pdfRuntime) => {
	const provider = pdfRuntime?.provider;
	if (!provider || typeof provider !== "object") return null;
	const status = String(provider.status || "").trim().toLowerCase();
	if (status !== "unconfigured" && status !== "invalid_config") return null;

	const hintFile = String(provider.localConfigHintFile || "").trim();
	const exampleFile = String(provider.localConfigExampleFile || "").trim();
	if (!hintFile && !exampleFile) return null;

	return exampleFile
		? `Local setup: update ${hintFile || "the backend local env file"} using ${exampleFile} as the tracked example.`
		: `Local setup: update ${hintFile}.`;
};

const getPdfRuntimeModeLabel = (pdfRuntime) => {
	const provider = pdfRuntime?.provider;
	if (!provider || typeof provider !== "object") return null;
	const status = String(provider.status || "").trim().toLowerCase();
	// Only show a mode label after startup succeeds; before that, the setup hint
	// is the actionable signal and any lane/mode distinction would be premature.
	if (status !== "configured") return null;
	const label = String(provider.modeLabel || "").trim();
	return label || null;
};

const getPdfRuntimeModeSummary = (pdfRuntime) => {
	const provider = pdfRuntime?.provider;
	if (!provider || typeof provider !== "object") return null;
	const status = String(provider.status || "").trim().toLowerCase();
	if (status !== "configured") return null;
	const summary = String(provider.modeSummary || "").trim();
	return summary || null;
};

const IMPORT_REPORT_FIELD_LABELS = {
	501: "Baseline Budget",
	502: "Baseline Quantity (# of Increments)",
	503: "Cost per Increment",
	504: "Max Increase",
	505: "Max Decrease",
	total_existing_inventory: "Total # of Increments in Inventory",
	military_service_id: "Military Service",
	domain_id: "Domain",
	category_id: "Category",
	name: "Name",
	description: "Description",
	option_type_id: "Option Type",
	status_id: "Status",
	data_status_id: "Data Status",
	sort_id: "Sort Order",
	fielding_delay: "Fielding Delay",
	increment: "Increment",
	unit_cost_exponent: "Unit Cost Exponent",
	fixed_os_cost_per_year: "Fixed O&S Cost per Year",
	os_cost_per_unit: "O&S Cost per Unit",
	item_payload: "Item Payload",
};

const formatImportReportFieldName = (fieldName) => {
	if (fieldName == null) return "";
	const key = String(fieldName);
	return IMPORT_REPORT_FIELD_LABELS[key] || key;
};

const renderReportList = (items, formatter = (item) => item) => {
	if (!Array.isArray(items) || items.length === 0) return null;

	return (
		<ul style={reportListStyle}>
			{items.map((item, index) => (
				<li key={`${item}-${index}`}>{formatter(item)}</li>
			))}
		</ul>
	);
};

const renderFollowUpGroups = (groups) => {
	if (!Array.isArray(groups) || groups.length === 0) return null;

	return (
		<div style={{ marginTop: "0.35rem", display: "grid", gap: "0.5rem" }}>
			{groups.map((group) => {
				const items = Array.isArray(group?.items) ? group.items : [];
				if (!group?.title || items.length === 0) return null;

				return (
					<div key={group.id || group.title}>
						<div style={{ fontWeight: 600 }}>{group.title}</div>
						<ul style={reportListStyle}>
							{items.map((item, index) => (
								<li key={`${group.id || group.title}-${item?.key || index}`}>
									<div>
										{item?.sourceValue
											? `${item?.label || item?.message || "(missing label)"} (source value: ${item.sourceValue})`
											: item?.label || item?.message || "(missing label)"}
									</div>
									{item?.mappedValue && (
										<div style={{ fontSize: "0.85rem" }}>Mapped label: {item.mappedValue}</div>
									)}
								</li>
							))}
						</ul>
					</div>
				);
			})}
		</div>
	);
};

const renderDeveloperOnlyPanel = (title, content) => (
	<div style={developerOnlyPanelStyle}>
		<div style={developerOnlyBadgeStyle}>Developer-only</div>
		<div style={{ fontWeight: 600 }}>{title}</div>
		{content}
	</div>
);

const renderImportReport = (importReport, onClearReport) => {
	if (!importReport) return null;

	return (
		<Box mb={3} p={3} borderWidth="1px" borderColor="blackAlpha.200" borderRadius="md">
			<div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Item Import Report</div>
			<div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
				<div>Inserted: {importReport?.insertedCount ?? 0}</div>
				<div>Rejected: {importReport?.rejectedCount ?? 0}</div>
				<div>Periods: {importReport?.numberOfPeriods ?? "-"}</div>
			</div>
			{Array.isArray(importReport?.results) && importReport.results.length > 0 && (
				<div style={{ display: "grid", gap: "0.75rem" }}>
					{importReport.results.map((result, index) => {
						const itemTitle = result?.sourceItemName || result?.sourceItemKey || `Item ${index + 1}`;
						const effectiveStatus =
							result?.effectiveDataStatusLabel || result?.effectiveDataStatusId || "Not inserted";
						const militaryServiceLabel = result?.militaryServiceLabel || "(required)";
						const domainLabel = result?.domainLabel || "(required)";
						const categoryLabel = result?.categoryLabel || "(required)";

						return (
							<Box
								key={`${result?.sourceItemKey || "item"}-${index}`}
								p={3}
								borderWidth="1px"
								borderColor="blackAlpha.100"
								borderRadius="md"
								bg={result?.inserted ? "white" : "blackAlpha.50"}
							>
								<div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
									<div style={{ fontWeight: 600 }}>{itemTitle}</div>
									<div>{result?.inserted ? `Inserted as #${result?.insertedItemId}` : "Rejected"}</div>
								</div>
								<div style={{ marginTop: "0.5rem", display: "grid", gap: "0.25rem" }}>
									<div>Effective data status: {effectiveStatus}</div>
									<div>Military Service: {militaryServiceLabel}</div>
									<div>Domain: {domainLabel}</div>
									<div>Category: {categoryLabel}</div>
								</div>
								{Array.isArray(result?.manualRequiredFields) && result.manualRequiredFields.length > 0 && (
									<div style={{ marginTop: "0.75rem" }}>
										<div style={{ fontWeight: 600 }}>Manual follow-up fields</div>
										{renderReportList(result.manualRequiredFields, formatImportReportFieldName)}
									</div>
								)}
								{Array.isArray(result?.rejectedReasons) && result.rejectedReasons.length > 0 && (
									<div style={{ marginTop: "0.75rem" }}>
										<div style={{ fontWeight: 600 }}>Rejected reasons</div>
										{renderReportList(result.rejectedReasons)}
									</div>
								)}
							</Box>
						);
					})}
				</div>
			)}
			<div style={{ marginTop: "0.75rem" }}>
				<Button variant="outline" onClick={onClearReport}>
					Clear Report
				</Button>
			</div>
		</Box>
	);
};

const renderIngestPreviewReport = (
	previewReport,
	selectedPreviewRowIds,
	onTogglePreviewRow,
	onSelectAllPreviewRows,
	onClearReport,
	onImportSelected,
	onImportAllReviewed,
	isImportingSelected,
	reviewCapabilities,
	onDownloadGeneratedJson,
	reviewPageM1,
	reviewRowsPerPage,
	onReviewPageChange,
	onReviewRowsPerPageChange,
	reviewRowsPerPageCollection,
	showTechnicalDetails = false
) => {
	if (!previewReport) return null;

	const summary = previewReport?.summary || {};
	const candidateItemsCount = summary?.candidateItems;
	const parseJob = previewReport?.parseJob || null;
	const parseJobStatus = String(parseJob?.status || "").toLowerCase();
	const intakeRouting = previewReport?.intakeRouting || {};
	const intakeReadiness = previewReport?.intakeReadiness || {};
	const previewItems = Array.isArray(previewReport?.previewItems) ? previewReport.previewItems : [];
	const selectablePreviewItems = previewItems.filter((row) => isPreviewRowImportAllowed(row));
	const normalizedReviewRowsPerPage = Number(reviewRowsPerPage) || 10;
	const reviewPageCount = Math.max(1, Math.ceil(previewItems.length / normalizedReviewRowsPerPage));
	const safeReviewPageM1 = Math.min(Math.max(Number(reviewPageM1) || 1, 1), reviewPageCount);
	const reviewStartIndex = previewItems.length === 0 ? 0 : (safeReviewPageM1 - 1) * normalizedReviewRowsPerPage;
	const pagedPreviewItems = previewItems.slice(reviewStartIndex, reviewStartIndex + normalizedReviewRowsPerPage);
	const reviewEndIndex = previewItems.length === 0 ? 0 : reviewStartIndex + pagedPreviewItems.length;
	const selectedPreviewIdSet = new Set(
		Array.isArray(selectedPreviewRowIds)
			? selectedPreviewRowIds.map((value) => String(value))
			: []
	);
	const isGeneratedJsonReview = String(previewReport?.ingestRun?.fileType || "").toLowerCase() === "json";
	const isScaffoldFallback = Boolean(previewReport?.ingestRun?.scaffoldMode);
	const canImportReviewedRows = Boolean(reviewCapabilities?.canImportReviewedRows);
	const canSelectReviewedRows = Boolean(reviewCapabilities?.canSelectReviewedRows);
	const isReadOnlyReview = Boolean(reviewCapabilities?.isReadOnlyReview);
	const hasFinalResultPayload = Boolean(previewReport?._finalResultPayloadLoaded);
	// The parse worker can succeed before the backend Stage 3 review payload is
	// rebuilt and fetched, so keep the waiting state visible until the ready-mode
	// payload replaces this async-start scaffold.
	const reviewIsStillLoading =
		!hasFinalResultPayload &&
		previewReport?.ingestRun?.orchestrationMode === "async_parse_job_started" &&
		(parseJobStatus === "queued" || parseJobStatus === "running" || parseJobStatus === "succeeded");
	const parseJobStartedAtMs = parseJob?.startedAt ? Date.parse(parseJob.startedAt) : Number.NaN;
	const parseJobFinishedAtMs = parseJob?.finishedAt ? Date.parse(parseJob.finishedAt) : Number.NaN;
	const elapsedProcessingLabel =
		reviewIsStillLoading && Number.isFinite(parseJobStartedAtMs)
			? formatElapsedTimerLabel(Date.now() - parseJobStartedAtMs)
			: null;
	// Keep the final parse duration visible after the ready payload replaces the
	// async-loading shell so long-running PDFs can still be reviewed honestly.
	const completedProcessingLabel =
		!reviewIsStillLoading &&
		!isGeneratedJsonReview &&
		Number.isFinite(parseJobStartedAtMs) &&
		Number.isFinite(parseJobFinishedAtMs) &&
		parseJobFinishedAtMs >= parseJobStartedAtMs
			? formatElapsedTimerLabel(parseJobFinishedAtMs - parseJobStartedAtMs)
			: null;
	const routingLaneLabel = getPendingRoutingLaneLabel(intakeRouting, reviewIsStillLoading);
	const pendingFilenameHintServiceBranch = reviewIsStillLoading
		? previewReport?.compatibility?.filenameHintServiceBranch || null
		: null;
	const parseJobStatusLabel =
		parseJobStatus === "queued"
			? "queued"
			: parseJobStatus === "running"
				? "running"
				: parseJobStatus === "succeeded"
					? "finalizing review"
					: parseJob?.status || "unknown";
	const candidateItemsDisplay =
		reviewIsStillLoading
			? "processing"
			: parseJobStatus === "failed"
				? "processing failed"
				: typeof candidateItemsCount === "number"
					? String(candidateItemsCount)
					: "pending";
	const reviewStatusText = reviewIsStillLoading
		? "Review is preparing in the background."
		: isScaffoldFallback
			? "Scaffold fallback is loaded."
			: "Review rows are ready.";
	const handlingText = isGeneratedJsonReview
		? isScaffoldFallback
			? "RAVEN accepted this JSON upload but kept it in the supported scaffold lane because authoritative Stage 2 route/readiness evidence was not strong enough for Stage 3 review rows."
			: "RAVEN accepted this supported non-PDF JSON payload and prepared the Stage 3 review rows automatically."
		: "RAVEN processed the uploaded PDF and prepared the review rows.";
	const followUpText = reviewIsStillLoading
		? "RAVEN is still preparing the review results for this file."
		: isScaffoldFallback && intakeReadiness?.status === "needs_manual_review"
			? "Supply or preserve authoritative Stage 2 route/readiness metadata before full Stage 3 review can continue."
		: intakeReadiness?.status === "needs_field_enrichment"
			? "Some reviewed items still need follow-up after import before they become Ready for downstream use."
			: intakeReadiness?.status === "blocked_source_or_parse_issue"
				? "RAVEN could not finish preparing this file for review."
				: intakeReadiness?.status === "needs_manual_review"
					? "This file needs manual review before Stage 3 review can continue."
					: intakeReadiness?.status === "ready_with_variant_rules" || intakeReadiness?.status === "ready_via_safe_fallback"
						? "Reviewed items can be imported now, but some may still need follow-up before they become Ready for downstream use."
						: "Reviewed items can be imported now, and any incomplete fields can be followed up afterward.";
	const technicalWarnings = [
		...(Array.isArray(previewReport?.compatibility?.reviewWarnings)
			? previewReport.compatibility.reviewWarnings
			: []),
		...(Array.isArray(previewReport?.reviewWarnings) ? previewReport.reviewWarnings : []),
	];
	const technicalMessages = Array.isArray(previewReport?.operatorMessages)
		? previewReport.operatorMessages
		: [];
	const reviewRowsPerPageItems = Array.isArray(reviewRowsPerPageCollection?.items)
		? reviewRowsPerPageCollection.items
		: reviewRowsPerPageOptions.map((value) => ({
			label: String(value),
			value: String(value),
		}));
	const reviewRowsPerPageSelectCollection =
		reviewRowsPerPageCollection && Array.isArray(reviewRowsPerPageCollection.items)
			? reviewRowsPerPageCollection
			: createListCollection({ items: reviewRowsPerPageItems });
	const allPreviewRowsSelected =
		selectablePreviewItems.length > 0 &&
		selectablePreviewItems.every((row) => selectedPreviewIdSet.has(String(row?.previewRowId || "")));
	const somePreviewRowsSelected = selectedPreviewIdSet.size > 0 && !allPreviewRowsSelected;

	return (
		<Box mb={3} p={3} borderWidth="1px" borderColor="blackAlpha.200" borderRadius="md" bg="blackAlpha.50">
			<div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Ingest Review</div>
			{isReadOnlyReview && (
				<Box
					p={3}
					borderWidth="1px"
					borderColor="blackAlpha.200"
					borderRadius="md"
					bg="white"
					style={{ marginBottom: "0.75rem" }}
				>
					<div style={{ fontWeight: 600 }}>Review only</div>
					<div style={{ marginTop: "0.25rem", ...reviewEntryTextStyle }}>
						Inspect the candidate rows, summary counts, warnings, and follow-up details here. Import actions
						and row selection are not available in this view.
					</div>
				</Box>
			)}
			{!isReadOnlyReview && canImportReviewedRows && !canSelectReviewedRows && (
				<Box
					p={3}
					borderWidth="1px"
					borderColor="blackAlpha.200"
					borderRadius="md"
					bg="white"
					style={{ marginBottom: "0.75rem" }}
				>
					<div style={{ fontWeight: 600 }}>Import all reviewed rows together</div>
					<div style={{ marginTop: "0.25rem", ...reviewEntryTextStyle }}>
						This view imports all reviewed rows together. Row-by-row selection and mapping assistance are not
						available here.
					</div>
				</Box>
			)}
			{reviewIsStillLoading && (
				<Box
					p={3}
					borderWidth="1px"
					borderColor="blackAlpha.200"
					borderRadius="md"
					bg="white"
					style={{ marginBottom: "0.75rem" }}
				>
					<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
						<Spinner size="sm" />
						<div style={{ display: "grid", gap: "0.2rem" }}>
							<div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
								<div style={{ fontWeight: 600 }}>Processing PDF in background</div>
								{elapsedProcessingLabel && (
									<div style={{ ...reviewEntryTextStyle, opacity: 0.75 }}>
										Started {elapsedProcessingLabel} ago
									</div>
								)}
							</div>
							<div style={reviewEntryTextStyle}>
								Status: {parseJobStatusLabel}. Review rows will load automatically when ready.
							</div>
						</div>
					</div>
				</Box>
			)}
			<div style={{ display: "grid", gap: "0.35rem" }}>
				<div style={reviewEntryTextStyle}>File: {previewReport?.ingestRun?.filename || "(unknown)"}</div>
				<div style={reviewEntryTextStyle}>Profile: {getSourceProfileDisplayLabel(previewReport?.sourceProfile) || "(unknown)"}</div>
				<div style={reviewEntryTextStyle}>Review status: {reviewStatusText}</div>
				<div style={reviewEntryTextStyle}>How this file was handled: {handlingText}</div>
				<div style={reviewEntryTextStyle}>Follow-up: {followUpText}</div>
				{completedProcessingLabel && (
					<div style={reviewEntryTextStyle}>Processing time: {completedProcessingLabel}</div>
				)}
				{!reviewIsStillLoading && <div style={reviewEntryTextStyle}>Candidate items: {candidateItemsDisplay}</div>}
			</div>
			<div
				style={{
					marginTop: "0.75rem",
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
					gap: "0.5rem",
				}}
			>
				<Box
					p={2}
					borderRadius="md"
					bg="white"
					borderWidth="1px"
					borderColor="blackAlpha.100"
					style={reviewIsStillLoading ? { opacity: 0.65 } : undefined}
				>
					<div style={{ ...reviewEntryTextStyle, opacity: 0.7 }}>Ready</div>
					<div style={{ fontWeight: 600 }}>{reviewIsStillLoading ? "..." : summary?.readyCandidates ?? 0}</div>
				</Box>
				<Box
					p={2}
					borderRadius="md"
					bg="white"
					borderWidth="1px"
					borderColor="blackAlpha.100"
					style={reviewIsStillLoading ? { opacity: 0.65 } : undefined}
				>
					<div style={{ ...reviewEntryTextStyle, opacity: 0.7 }}>Partial</div>
					<div style={{ fontWeight: 600 }}>{reviewIsStillLoading ? "..." : summary?.partialCandidates ?? 0}</div>
				</Box>
				<Box
					p={2}
					borderRadius="md"
					bg="white"
					borderWidth="1px"
					borderColor="blackAlpha.100"
					style={reviewIsStillLoading ? { opacity: 0.65 } : undefined}
				>
					<div style={{ ...reviewEntryTextStyle, opacity: 0.7 }}>Unresolved</div>
					<div style={{ fontWeight: 600 }}>{reviewIsStillLoading ? "..." : summary?.unresolvedCandidates ?? 0}</div>
				</Box>
				<Box
					p={2}
					borderRadius="md"
					bg="white"
					borderWidth="1px"
					borderColor="blackAlpha.100"
					style={reviewIsStillLoading ? { opacity: 0.65 } : undefined}
				>
					<div style={{ ...reviewEntryTextStyle, opacity: 0.7 }}>Total</div>
					<div style={{ fontWeight: 600 }}>{reviewIsStillLoading ? "..." : summary?.totalSourceItems ?? 0}</div>
				</Box>
			</div>
			{parseJob &&
				showTechnicalDetails &&
				renderDeveloperOnlyPanel(
					"Parse job",
					<div style={{ display: "grid", gap: "0.25rem" }}>
						<div>Job ID: {parseJob.jobId || "(unknown)"}</div>
						{parseJob.error && <div>Error: {parseJob.error}</div>}
					</div>
				)}
			{showTechnicalDetails &&
				technicalWarnings.length > 0 &&
				renderDeveloperOnlyPanel("Review warnings", renderReportList(technicalWarnings))}
			<div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
				{canSelectReviewedRows && previewItems.length > 0 && (
					<Button
						size="2xs"
						style={uploadButtonStyle}
						onClick={onImportSelected}
						disabled={isImportingSelected || selectedPreviewIdSet.size === 0}
					>
						{isImportingSelected ? "Importing..." : `Import Selected (${selectedPreviewIdSet.size})`}
					</Button>
				)}
				{!canSelectReviewedRows && canImportReviewedRows && previewItems.length > 0 && (
					<Button
						size="2xs"
						style={uploadButtonStyle}
						onClick={onImportAllReviewed}
						disabled={isImportingSelected || selectablePreviewItems.length === 0}
					>
						{isImportingSelected ? "Importing..." : `Import Reviewed Rows (${selectablePreviewItems.length})`}
					</Button>
				)}
				<Button size="2xs" variant="outline" style={uploadButtonStyle} onClick={onClearReport}>
					{reviewIsStillLoading ? "Start Over" : "Clear Preview"}
				</Button>
				{hasFinalResultPayload && parseJob?.jobId && String(parseJob.status || "").toLowerCase() === "succeeded" && (
					<Button
						size="2xs"
						variant="outline"
						style={uploadButtonStyle}
						onClick={() => {
							if (typeof onDownloadGeneratedJson === "function") {
								onDownloadGeneratedJson(previewReport, parseJob);
							}
						}}
					>
						Download Generated JSON
					</Button>
				)}
			</div>
				{showTechnicalDetails &&
					renderDeveloperOnlyPanel(
						"Review diagnostics",
						<div style={{ display: "grid", gap: "0.35rem" }}>
							<div style={reviewEntryTextStyle}>Routing lane: {routingLaneLabel}</div>
							{pendingFilenameHintServiceBranch && (
								<div style={reviewEntryTextStyle}>
									Filename hint: {pendingFilenameHintServiceBranch} (diagnostic only)
								</div>
							)}
							{intakeReadiness?.status && <div style={reviewEntryTextStyle}>Intake readiness: {intakeReadiness.status}</div>}
							<div style={reviewEntryTextStyle}>
							Compatibility: {previewReport?.compatibility?.classification || "unknown"} (
							{previewReport?.compatibility?.confidence || "n/a"})
						</div>
						{intakeReadiness?.summary && (
								<div style={reviewEntryTextStyle}>Readiness summary: {intakeReadiness.summary}</div>
						)}
						{intakeRouting?.routingReason && (
								<div style={reviewEntryTextStyle}>Routing reason: {intakeRouting.routingReason}</div>
						)}
						{technicalMessages.length > 0 && (
							<div style={{ marginTop: "0.5rem" }}>
									<div style={reviewEntryTextStyle}>Technical details</div>
								{renderReportList(technicalMessages)}
							</div>
						)}
						</div>
					)}
			{previewItems.length > 0 && (
				<div style={{ marginTop: "0.75rem", display: "grid", gap: "0.5rem" }}>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr auto 1fr",
							alignItems: "end",
							gap: "0.75rem",
						}}
					>
						<div style={{ display: "grid", gap: "0.35rem", minHeight: "48px", alignContent: "end" }}>
								<div style={{ fontWeight: 600 }}>Preview rows</div>
								<div style={reviewEntryTextStyle}>
								Results {reviewStartIndex + 1}-{reviewEndIndex} of {previewItems.length}
							</div>
							{canSelectReviewedRows && (
								<Checkbox.Root
									checked={somePreviewRowsSelected ? "indeterminate" : allPreviewRowsSelected}
									onCheckedChange={(next) => {
										const checked =
											next === true ||
											next === "indeterminate" ||
											next?.checked === true ||
											next?.detail?.checked === true;
										onSelectAllPreviewRows(checked);
									}}
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: "0.5rem",
										minHeight: "26px",
									}}
								>
									<Checkbox.HiddenInput aria-checked={somePreviewRowsSelected ? "mixed" : undefined} />
									<Checkbox.Control
										data-indeterminate={somePreviewRowsSelected ? "" : undefined}
										data-state={
											somePreviewRowsSelected
												? "indeterminate"
												: allPreviewRowsSelected
													? "checked"
													: "unchecked"
										}
									/>
									<div style={{ fontWeight: 600 }}>
										Select all reviewed ({selectablePreviewItems.length})
									</div>
								</Checkbox.Root>
							)}
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "flex-end",
								justifyContent: "center",
								gap: "0.75rem",
								minHeight: "48px",
							}}
						>
							<Pagination.Root
								count={previewItems.length}
								pageSize={normalizedReviewRowsPerPage}
								page={safeReviewPageM1}
								onPageChange={({ page }) => onReviewPageChange(page)}
							>
								<ButtonGroup variant="ghost" size="2xs">
									<Pagination.PrevTrigger asChild>
										<IconButton>
											<ChevronLeftRegular />
										</IconButton>
									</Pagination.PrevTrigger>
									<Pagination.Items
										render={(page) => (
											<IconButton variant={{ base: "ghost", _selected: "solid" }}>
												{page.value}
											</IconButton>
										)}
									/>
									<Pagination.NextTrigger asChild>
										<IconButton>
											<ChevronRightRegular />
										</IconButton>
									</Pagination.NextTrigger>
								</ButtonGroup>
							</Pagination.Root>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								minHeight: "48px",
								alignItems: "flex-end",
								justifySelf: "end",
							}}
						>
							<div style={{ display: "grid", gap: "5px", width: "66px" }}>
								<div style={{ ...reviewEntryTextStyle, lineHeight: 1 }}>Results</div>
								<MetreaSelectRoot
									data-raven-compact-select="true"
									variant="outline"
									collection={reviewRowsPerPageSelectCollection}
									size="xs"
									name="review_rows_per_page"
									width="100%"
									value={[String(normalizedReviewRowsPerPage)]}
									onValueChange={(details) => {
										const nextValue = Array.isArray(details?.value)
											? Number(details.value[0] ?? normalizedReviewRowsPerPage)
											: normalizedReviewRowsPerPage;
										onReviewRowsPerPageChange(nextValue);
									}}
								>
									<MetreaSelectTrigger
										id="baseline-items-review-rows-per-page"
										style={{
											width: "100%",
											fontSize: "0.85rem",
											lineHeight: "1rem",
										}}
									>
										<MetreaSelectValueText>
											{(selectedItems) => selectedItems?.[0]?.label || "Results"}
										</MetreaSelectValueText>
									</MetreaSelectTrigger>
									<Portal>
										<MetreaSelectContent>
											{reviewRowsPerPageItems.map((item) => (
												<MetreaSelectItem key={item.value} item={item}>
													{item.label}
												</MetreaSelectItem>
											))}
										</MetreaSelectContent>
									</Portal>
								</MetreaSelectRoot>
							</div>
						</div>
					</div>
					{pagedPreviewItems.map((row, index) => {
						const rowKey = row?.previewRowId || `preview-row-${index + 1}`;
						const statusLabel = row?.candidateReport?.effectiveDataStatusLabel || "Unknown";
						const followUpGroups = Array.isArray(row?.candidateReport?.followUpGroups)
							? row.candidateReport.followUpGroups
							: [];
						const mappingDiagnostics = Array.isArray(row?.candidateReport?.mappingDiagnostics)
							? row.candidateReport.mappingDiagnostics
							: [];
						const importHandoff =
							row?.importHandoff && typeof row.importHandoff === "object" ? row.importHandoff : null;
						const isImportAllowed = Boolean(importHandoff?.importAllowed);
						const importBlockingReasons = Array.isArray(importHandoff?.blockingReasons)
							? importHandoff.blockingReasons
							: [];
						const followUp = Array.isArray(row?.candidateReport?.followUpMessages)
							? row.candidateReport.followUpMessages
							: [];
						const isSelected = row?.previewRowId
							? selectedPreviewIdSet.has(String(row.previewRowId))
							: false;
						return (
							<Box key={rowKey} p={2} borderWidth="1px" borderColor="blackAlpha.200" borderRadius="md" bg="white">
								<div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", alignItems: "flex-start" }}>
									{canSelectReviewedRows ? (
										<Checkbox.Root
											checked={isSelected}
											disabled={!isImportAllowed}
											onCheckedChange={(next) => {
												const checked =
													next === true ||
													next?.checked === true ||
													next?.detail?.checked === true;
												if ((checked && !isSelected) || (!checked && isSelected)) {
													onTogglePreviewRow(row?.previewRowId);
												}
											}}
											style={{ display: "inline-flex", alignItems: "flex-start", gap: "0.5rem", flex: 1 }}
										>
											<Checkbox.HiddenInput />
											<Checkbox.Control />
											<div style={{ fontWeight: 600 }}>
												{row?.sourceItemName || row?.candidateItem?.name || row?.sourceItemKey || "(unnamed)"}
											</div>
										</Checkbox.Root>
									) : (
										<div style={{ fontWeight: 600, flex: 1 }}>
											{row?.sourceItemName || row?.candidateItem?.name || row?.sourceItemKey || "(unnamed)"}
										</div>
									)}
									<div>{statusLabel}</div>
								</div>
								{canSelectReviewedRows && (
									<>
										<div style={{ marginTop: "0.35rem", ...reviewEntryTextStyle }}>
											{isImportAllowed
												? isSelected
													? "Selected for import"
													: "Not selected for import"
												: "This review row cannot be imported from the current state"}
										</div>
										<div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
											<div style={{ fontWeight: 600 }}>
												Import handoff: {isImportAllowed ? "allowed" : "blocked"}
											</div>
										</div>
									</>
								)}
								{row?.sourceItemKey && (
									<div style={{ marginTop: "0.25rem", ...reviewEntryTextStyle }}>
										Source key: {row.sourceItemKey}
									</div>
								)}
								{mappingDiagnostics.length > 0 && (
									<div style={{ marginTop: "0.35rem" }}>
										<div style={{ fontWeight: 600 }}>Mapping diagnostics</div>
										{renderReportList(mappingDiagnostics)}
									</div>
								)}
								{importBlockingReasons.length > 0 && (
									<div style={{ marginTop: "0.35rem" }}>
										<div style={{ fontWeight: 600 }}>Import blocked</div>
										{renderReportList(importBlockingReasons)}
									</div>
								)}
								{followUpGroups.length > 0 ? (
									<div style={{ marginTop: "0.35rem" }}>{renderFollowUpGroups(followUpGroups)}</div>
								) : followUp.length > 0 ? (
									<div style={{ marginTop: "0.35rem" }}>{renderReportList(followUp)}</div>
								) : null}
							</Box>
						);
					})}
				</div>
			)}
		</Box>
	);
};

const getDefaultSelectedPreviewRowIds = (previewReport) => {
	const previewItems = Array.isArray(previewReport?.previewItems) ? previewReport.previewItems : [];
	return previewItems
		.filter((row) => row?.selectedByDefault && row?.previewRowId)
		.map((row) => row.previewRowId);
};

const isPreviewRowImportAllowed = (row) => Boolean(row?.importHandoff?.importAllowed);

const getImportablePreviewRows = (previewReport) => {
	const previewItems = Array.isArray(previewReport?.previewItems) ? previewReport.previewItems : [];
	return previewItems.filter((row) => isPreviewRowImportAllowed(row));
};

const buildSelectedImportItems = (previewReport, selectedPreviewRowIds) => {
	const selectedIds = new Set(
		Array.isArray(selectedPreviewRowIds)
			? selectedPreviewRowIds.map((value) => String(value))
			: []
	);
	const previewItems = Array.isArray(previewReport?.previewItems) ? previewReport.previewItems : [];

	return previewItems
		// Preserve the backend-owned review handoff when forwarding selected rows
		// so final import can re-validate that these still represent
		// backend-approved Stage 3 review results.
		.filter(
			(row) =>
				row?.previewRowId &&
				selectedIds.has(String(row.previewRowId)) &&
				isPreviewRowImportAllowed(row)
		)
		.map((row) => ({
			...(row?.candidateItem && typeof row.candidateItem === "object" ? row.candidateItem : {}),
			reviewImportHandoff:
				row?.importHandoff && typeof row.importHandoff === "object" ? row.importHandoff : null,
		}))
		.filter((item) => item && typeof item === "object");
};

const normalizeParsedLineItemToImportItem = (lineItem, parentKey, index) => {
	const title = typeof lineItem?.Title === "string" ? lineItem.Title.trim() : "";
	const lineNumber = typeof lineItem?.LineItemNumber === "string" ? lineItem.LineItemNumber.trim() : "";
	const description = typeof lineItem?.Description === "string" ? lineItem.Description : "";

	const fallbackName = lineNumber || `Parsed Item ${index + 1}`;
	const name = title || fallbackName;
	const sourceItemKey = lineNumber || `${parentKey || "generated"}-${index + 1}`;

	return {
		name,
		description,
		sourceItemKey,
		sourceValues: {
			source_profile: "existing_procurement_program_generated_json",
			source_collection: parentKey || null,
			line_item_number: lineNumber || null,
			appropriation_code: lineItem?.AppropriationCode || null,
			appropriation_name: lineItem?.AppropriationName || null,
			budget_activity_code: lineItem?.BudgetActivityCode || null,
			budget_activity: lineItem?.BudgetActivity || null,
			budget_sub_activity_code: lineItem?.BudgetSubActivityCode || null,
			budget_sub_activity: lineItem?.BudgetSubActivity || null,
		},
	};
};

const extractImportItemsFromUploadPayload = (loadedFile) => {
	// Keep backward compatibility with the existing import payload contract.
	if (Array.isArray(loadedFile?.items)) {
		return {
			items: loadedFile.items,
			wasConvertedFromGeneratedJson: false,
		};
	}

	if (!loadedFile || typeof loadedFile !== "object") {
		return {
			items: null,
			wasConvertedFromGeneratedJson: false,
		};
	}

	const candidateKeys = Object.keys(loadedFile || {});
	const converted = [];

	// Support parser-generated JSON shape by flattening each top-level block's
	// Line_Items rows into importable baseline item records.
	candidateKeys.forEach((key) => {
		const block = loadedFile[key];
		if (!block || typeof block !== "object") return;
		const lineItems = Array.isArray(block.Line_Items) ? block.Line_Items : null;
		if (!lineItems || lineItems.length === 0) return;

		lineItems.forEach((lineItem, index) => {
			const hasRecognizedShape =
				!!lineItem &&
				typeof lineItem === "object" &&
				(
					typeof lineItem.Title === "string" ||
					typeof lineItem.LineItemNumber === "string" ||
					typeof lineItem.AppropriationCode === "string"
				);
			if (!hasRecognizedShape) return;
			converted.push(normalizeParsedLineItemToImportItem(lineItem, key, index));
		});
	});

	if (converted.length > 0) {
		return {
			items: converted,
			wasConvertedFromGeneratedJson: true,
		};
	}

	return {
		items: null,
		wasConvertedFromGeneratedJson: false,
	};
};

const BaselineItems = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	// Custom hook abstracts Redux logic for data retrieval
	const { fetchBaselineByIdOrUuid } = useBaselinesData();

	const pageMode = props.mode;
	const uiSize = props.size;
	const { showDialog, withConfirmation, withError } = useDialogBox();
	// Keep the staged review rollout centralized so each gate can be flipped
	// intentionally without scattering adjacent booleans across the render tree.
	const ingestRolloutCapabilities = buildBaselineItemIngestRolloutCapabilities();

	const selectedBaseline = useSelector((state) => state.baselines.selectedBaseline);
	const [currBaseline, setCurrBaseline] = useState(selectedBaseline);
	useEffect(() => {
		debugLog(2, "[BaselineItems][FLOW] mounted", {});
	}, []);

	useEffect(() => {
		setCurrBaseline(selectedBaseline);
		const nextBaselineId = selectedBaseline?.id ?? null;
		if (previousSelectedBaselineIdRef.current !== nextBaselineId) {
			setIngestPreviewReport(null);
			setSelectedPreviewRowIds([]);
			setReviewPageM1(1);
			parseJobNotifiedRef.current.clear();
			parseJobResultRequestedRef.current.clear();
			parseJobResultResolvedRef.current.clear();
			parseJobPollErrorShownRef.current.clear();
		}
		previousSelectedBaselineIdRef.current = nextBaselineId;
		debugLog(3, "[BaselineItems][STATE] selectedBaseline changed", {
			baselineId: selectedBaseline?.id ?? null,
		});
	}, [selectedBaseline]);

	// Custom hook to abstract fetch/select logic (encapsulates Redux for reusability/testability)
	const { lookups } = useLookupsData();

	const {
		currentItem,
		setCurrentItem,
		handleSelectBaselineItem,
		clearCurrentItem,
	} = useBaselineItemsData(currBaseline, lookups, fetchBaselineByIdOrUuid, props.onSetDataChanged);

	const { deleteItemAction, updateItemStatusAction } = useBaselineItemActions({
		fetchBaselineByIdOrUuid,
		handleBackToList: clearCurrentItem,
		currBaseline,
		localItem: currentItem,
		setLocalItem: setCurrentItem,
		lookups,
	});

	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("name");
	const [pageM1, setPageM1] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [importReport, setImportReport] = useState(null);
	const [isImportingItems, setIsImportingItems] = useState(false);
	const [itemIngestProfiles, setItemIngestProfiles] = useState([]);
	const [itemIngestPdfRuntime, setItemIngestPdfRuntime] = useState(null);
	const [useMappingFallbackForReview, setUseMappingFallbackForReview] = useState(false);
	const [isPdfProfileModalOpen, setIsPdfProfileModalOpen] = useState(false);
	const [pendingPdfUpload, setPendingPdfUpload] = useState(null);
	const [pendingPdfProfileId, setPendingPdfProfileId] = useState("");
	const [isGeneratedJsonProfileModalOpen, setIsGeneratedJsonProfileModalOpen] = useState(false);
	const [pendingGeneratedJsonUpload, setPendingGeneratedJsonUpload] = useState(null);
	const [pendingGeneratedJsonProfileId, setPendingGeneratedJsonProfileId] = useState("");
	const [ingestPreviewReport, setIngestPreviewReport] = useState(null);
	const [selectedPreviewRowIds, setSelectedPreviewRowIds] = useState([]);
	const [isPreparingIngestPreview, setIsPreparingIngestPreview] = useState(false);
	const [reviewPageM1, setReviewPageM1] = useState(1);
	const [reviewRowsPerPage, setReviewRowsPerPage] = useState(10);
	const [, setParseJobTimerTick] = useState(0);
	const parseJobNotifiedRef = useRef(new Set());
	const parseJobResultRequestedRef = useRef(new Set());
	const parseJobResultResolvedRef = useRef(new Set());
	const parseJobPollErrorShownRef = useRef(new Set());
	const activeParseJobRef = useRef({ baselineId: null, jobId: null });
	const previousSelectedBaselineIdRef = useRef(selectedBaseline?.id ?? null);

	const ingestProfileCollection = useMemo(
		() =>
			createListCollection({
				items: [
					{ label: "Select an ingest profile...", value: "" },
					...itemIngestProfiles.map((profile) => ({
						label: getSourceProfileDisplayLabel(profile),
						value: String(profile?.id || ""),
					})),
				],
			}),
		[itemIngestProfiles],
	);
	const reviewRowsPerPageCollection = useMemo(
		() =>
			createListCollection({
				items: reviewRowsPerPageOptions.map((value) => ({
					label: String(value),
					value: String(value),
				})),
			}),
		[],
	);
	const effectiveUseMappingFallbackForReview =
		ingestRolloutCapabilities.canUseMappingAssistance && useMappingFallbackForReview;
	const showTechnicalDetails = isDeveloper();

	useEffect(() => {
		activeParseJobRef.current = {
			baselineId: currBaseline?.id ?? null,
			jobId: ingestPreviewReport?.parseJob?.jobId ?? null,
		};
	}, [currBaseline?.id, ingestPreviewReport?.parseJob?.jobId]);

	useEffect(() => {
		if (ingestRolloutCapabilities.canUseMappingAssistance) return;
		setUseMappingFallbackForReview(false);
	}, [ingestRolloutCapabilities.canUseMappingAssistance]);

	useEffect(() => {
		if (pageMode !== "edit") return;

		let isActive = true;
		const getProfilesRequest =
			typeof BaselineService.getItemIngestProfiles === "function"
				? BaselineService.getItemIngestProfiles()
				: null;

		// Keep Step 3 usable in test and non-PDF lanes even if this optional
		// preload hook is not mocked or not yet wired in a local harness.
		if (!getProfilesRequest || typeof getProfilesRequest.then !== "function") {
			setItemIngestProfiles([]);
			return () => {
				isActive = false;
			};
		}

		getProfilesRequest
			.then((response) => {
				if (!isActive) return;
				const profiles = Array.isArray(response?.data?.profiles) ? response.data.profiles : [];
				setItemIngestProfiles(profiles);
				setItemIngestPdfRuntime(
					response?.data?.pdfRuntime && typeof response.data.pdfRuntime === "object"
						? response.data.pdfRuntime
						: null
				);
			})
			.catch((error) => {
				if (!isActive) return;
				setItemIngestPdfRuntime(null);
				withError(error, null, {
					title: "PDF Ingest Profiles Unavailable",
					fallbackMessage: "Failed to load PDF ingest profiles.",
				});
			});

		return () => {
			isActive = false;
		};
	}, [pageMode, withError]);

	useEffect(() => {
		const baselineId = currBaseline?.id;
		const jobId = ingestPreviewReport?.parseJob?.jobId;
		const status = String(ingestPreviewReport?.parseJob?.status || "").toLowerCase();
		const parseJobError = ingestPreviewReport?.parseJob?.error;
		if (!jobId || !baselineId) return;
		let cancelled = false;
		const shouldApplyParseJobResult = () => {
			const activeParseJob = activeParseJobRef.current;
			return activeParseJob.baselineId === baselineId && activeParseJob.jobId === jobId;
		};

		const fetchReviewResult = () => {
			if (parseJobResultResolvedRef.current.has(jobId)) {
				return;
			}
			if (parseJobResultRequestedRef.current.has(jobId)) {
				return;
			}
			parseJobResultRequestedRef.current.add(jobId);

			BaselineService.getItemIngestParseJobResult(baselineId, jobId)
				.then((response) => {
					if (!shouldApplyParseJobResult()) {
						parseJobResultRequestedRef.current.delete(jobId);
						return;
					}
					const reviewPayload = response?.data;
					if (!reviewPayload || typeof reviewPayload !== "object") {
						throw new Error("Review results were unavailable.");
					}
					const finalReviewPayload = {
						...reviewPayload,
						_finalResultPayloadLoaded: true,
					};

					parseJobResultResolvedRef.current.add(jobId);
					setIngestPreviewReport((prev) => {
						if (prev?.parseJob?.jobId && prev.parseJob.jobId !== jobId) {
							return prev;
						}
						return finalReviewPayload;
					});

					if (!parseJobNotifiedRef.current.has(jobId)) {
						parseJobNotifiedRef.current.add(jobId);
						showDialog({
							title: "PDF Review Ready",
							type: "success",
							message: [
								`Ready: ${reviewPayload?.summary?.readyCandidates ?? 0}`,
								`Partial: ${reviewPayload?.summary?.partialCandidates ?? 0}`,
								`Unresolved: ${reviewPayload?.summary?.unresolvedCandidates ?? 0}`,
								"RAVEN finished processing the file and the review results are ready.",
							],
						});
					}
				})
				.catch((error) => {
					if (!shouldApplyParseJobResult()) {
						parseJobResultRequestedRef.current.delete(jobId);
						return;
					}

					const statusCode = Number(error?.response?.status);
					const reviewPayload = error?.response?.data;
					const hasFinalResultPayload =
						reviewPayload &&
						typeof reviewPayload === "object" &&
						(reviewPayload?.parseJob?.jobId || reviewPayload?.intakeReadiness || Array.isArray(reviewPayload?.previewItems));

					if (statusCode === 409 && hasFinalResultPayload) {
						const finalReviewPayload = {
							...reviewPayload,
							_finalResultPayloadLoaded: true,
						};
						parseJobResultResolvedRef.current.add(jobId);
						setIngestPreviewReport((prev) => {
							if (prev?.parseJob?.jobId && prev.parseJob.jobId !== jobId) {
								return prev;
							}
							return finalReviewPayload;
						});

						if (!parseJobNotifiedRef.current.has(jobId)) {
							parseJobNotifiedRef.current.add(jobId);
							const readinessStatus = String(reviewPayload?.intakeReadiness?.status || "").trim();
							const investigationNeeded =
								readinessStatus === "needs_new_profile" ||
								readinessStatus === "needs_specific_family" ||
								readinessStatus === "needs_stage1_strategy" ||
								readinessStatus === "needs_profile_mapper" ||
								readinessStatus === "blocked_source_or_parse_issue";
							// A 409 here is not a transport failure. The backend is handing
							// back the final stop-state payload so operators can see why the
							// PDF stayed blocked and whether route/lane follow-through is
							// required for this document family.
							showDialog({
								title: investigationNeeded ? "PDF Review Needs Investigation" : "PDF Review Updated",
								type: "info",
								message: [
									reviewPayload?.intakeReadiness?.summary || "RAVEN updated the review results.",
									...(investigationNeeded
										? [
											"RAVEN stopped before Stage 3 continuation. Investigate the PDF/parser result and add or promote a Raven lane only if the data-repo proof supports it.",
										]
										: []),
								],
							});
						}
						return;
					}

					parseJobResultRequestedRef.current.delete(jobId);
					withError(error, null, {
						title: "PDF Review Result Unavailable",
						fallbackMessage: "Could not load review results for this PDF yet.",
					});
				});
		};

		if (status === "failed") {
			if (!parseJobNotifiedRef.current.has(jobId)) {
				parseJobNotifiedRef.current.add(jobId);
				withError(parseJobError || "PDF processing failed.", null, {
					title: "PDF Review Failed",
					fallbackMessage: "RAVEN could not process this PDF.",
				});
			}
			return;
		}

		const pollStatus = () => {
			if (parseJobResultResolvedRef.current.has(jobId)) {
				return;
			}
			if (status === "succeeded") {
				fetchReviewResult();
				return;
			}

			BaselineService.getItemIngestParseJobStatus(baselineId, jobId)
				.then((response) => {
					if (cancelled) return;
					if (parseJobResultResolvedRef.current.has(jobId)) return;
					const nextParseJob = response?.data?.parseJob;
					if (!nextParseJob) return;

					setIngestPreviewReport((prev) => {
						if (!prev?.parseJob || prev.parseJob.jobId !== jobId) {
							return prev;
						}
						if (parseJobResultResolvedRef.current.has(jobId)) {
							return prev;
						}
						return {
							...prev,
							parseJob: nextParseJob,
						};
					});

					const nextStatus = String(nextParseJob.status || "").toLowerCase();
					if (nextStatus === "succeeded") {
						fetchReviewResult();
					} else if (nextStatus === "failed" && !parseJobNotifiedRef.current.has(jobId)) {
						parseJobNotifiedRef.current.add(jobId);
						withError(nextParseJob.error || "PDF processing failed.", null, {
							title: "PDF Review Failed",
							fallbackMessage: "RAVEN could not process this PDF.",
						});
					}
				})
				.catch((error) => {
					if (cancelled) return;
					if (parseJobPollErrorShownRef.current.has(jobId)) return;
					parseJobPollErrorShownRef.current.add(jobId);
					withError(error, null, {
						title: "PDF Review Update Unavailable",
						fallbackMessage: "Could not refresh PDF review status.",
					});
				});
		};

		pollStatus();
		const id = setInterval(pollStatus, 5000);

		return () => {
			cancelled = true;
			clearInterval(id);
		};
	}, [
		// Polling should only restart when the tracked job identity or terminal
		// status changes. Depending on the whole parseJob object would retrigger
		// the effect after every status payload refresh and collapse the intended
		// 5-second cadence into near-immediate follow-up requests.
		ingestPreviewReport?.parseJob?.jobId,
		ingestPreviewReport?.parseJob?.status,
		ingestPreviewReport?.parseJob?.error,
		currBaseline?.id,
		showDialog,
		withError,
	]);

	useEffect(() => {
		setSelectedPreviewRowIds(getDefaultSelectedPreviewRowIds(ingestPreviewReport));
		setReviewPageM1(1);
	}, [ingestPreviewReport]);

	useEffect(() => {
		const isAsyncReviewLoading =
			Boolean(ingestPreviewReport?.parseJob?.jobId) &&
			!Boolean(ingestPreviewReport?._finalResultPayloadLoaded) &&
			ingestPreviewReport?.ingestRun?.orchestrationMode === "async_parse_job_started" &&
			["queued", "running", "succeeded"].includes(
				String(ingestPreviewReport?.parseJob?.status || "").toLowerCase()
			);
		if (!isAsyncReviewLoading) {
			return undefined;
		}

		const id = window.setInterval(() => {
			setParseJobTimerTick((prev) => prev + 1);
		}, 1000);

		return () => {
			window.clearInterval(id);
		};
	}, [
		ingestPreviewReport?._finalResultPayloadLoaded,
		ingestPreviewReport?.ingestRun?.orchestrationMode,
		ingestPreviewReport?.parseJob?.jobId,
		ingestPreviewReport?.parseJob?.status,
	]);

	useEffect(() => {
		setReviewPageM1(1);
	}, [reviewRowsPerPage]);

	/**
	 * Called when a Baseline Item is edited.
	 * Updates local item state and returns the update object for parent.
	 * @param {Object} item - Updated baseline item.
	 * @returns {Object} updateData - The prepared baseline object.
	 */
	const handleLocalItemChange = (item) => {
		debugLog(3, "[BaselineItems][ACTION] handleLocalItemChange", { item: item });

		setCurrentItem(item);

		const cloneBaseline = deepClone(currBaseline);

		// if NOT new Item
		/**
		 * Scenario: The user edits or saves a Baseline Item.
		 * We need to ensure that the latest version of this item is reflected at the start of the baseline_items array.
		 *
		 * - Remove any existing item with the same id (handles both edits and re-saves of the same item).
		 * - Unshift (insert at start) the updated item, so it is always the first in the list.
		 * - This prevents duplicates and ensures that the latest edit is visible at the top (helpful if sorting by "last updated").
		 *
		 * Without this merge pattern, you could have stale, duplicated, or out-of-order items after multiple edits.
		 */
		if (item && item.id) {
			cloneBaseline.baseline_items = cloneBaseline.baseline_items.filter((x) => {
				return x.id !== item.id;
			});
			cloneBaseline.baseline_items.unshift(item);
		}

		const updateData = {
			...cloneBaseline,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		// Notify parent component of changes.
		// Don't do it until the "Save" button clicked in BaselineItemConfigs
		// props.onLocalBaselineChange(updateData);
		return updateData;
	};

	const handleSaveBaseline = () => {
		debugLog(3, "[BaselineItems][ACTION] handleSaveBaseline", { step: -1 });
		debugLog(2, "[BaselineItems][FLOW] onSaveDataToDB started", { step: -1 });
		// Save baseline to DB directly without prompt dialog
		props.onSaveDataToDB(-1);
	};

	const handleClearComplete = () => {
		debugLog(3, "[BaselineItems][ACTION] handleClearComplete", {});
		props.onClearComplete();
	};

	const handleClearCurrentItem = () => {
		debugLog(3, "[BaselineItems][ACTION] handleClearCurrentItem", {});
		clearCurrentItem();
	};

	const handleAddItem = () => {
		debugLog(3, "[BaselineItems][ACTION] handleAddItem", {});

		const options = [...lookups.BL_ITEM_DETAILS];
		setCurrentItem({ ...initialBaselineItemState(currBaseline, options) });
	};

	const handleChangeStatusFromList = async (id, newStatus) => {
		debugLog(3, "[BaselineItems][ACTION] handleChangeStatusFromList", { id: id, newStatus: newStatus });

		debugLog(2, "[BaselineItems][FLOW] updateItemStatusAction started", { id: id, newStatus: newStatus });
		updateItemStatusAction(id, newStatus, false); // false: don't show confirmation MsgBox
	};

	const handleSoftDeleteFromList = (item) => {
		debugLog(3, "[BaselineItems][ACTION] handleSoftDeleteFromList", {
			id: item?.id ?? null,
			name: item?.name ?? null,
		});

		if (!item?.id) return;

		withConfirmation(
			{
				title: "Soft Delete Baseline Item",
				message: [
					`You are about to soft delete this Baseline Item, ${item.name}.`,
					"This will set status_id to -1, remove it from the normal UI, and require backend/database access to restore it.",
				],
				confirmLabel: "Soft Delete",
				cancelLabel: "Cancel",
			},
			() => updateItemStatusAction(item.id, -1, false),
		);
	};

	const handlePurgeFromList = (item) => {
		debugLog(3, "[BaselineItems][ACTION] handlePurgeFromList", {
			id: item?.id ?? null,
			name: item?.name ?? null,
		});

		if (!item?.id) return;

		withConfirmation(
			{
				title: "Purge Baseline Item",
				message: [
					`You are about to permanently purge this Baseline Item, ${item.name}.`,
					"This will physically delete the item data from the database and cannot be undone from the UI.",
				],
				confirmLabel: "Purge",
				cancelLabel: "Cancel",
			},
			() => deleteItemAction(item.id),
		);
	};

	const handleReloadBaseline = () => {
		debugLog(3, "[BaselineItems][ACTION] handleReloadBaseline", { currentItem: currentItem });
		debugLog(2, "[BaselineItems][FLOW] onReloadBaseline started", {
			baselineId: currentItem?.baseline_id ?? null,
		});
		if (currentItem?.id) props.onReloadBaseline(currentItem.baseline_id);
	};

	const handleSetPageM1 = (newPage) => {
		debugLog(3, "[BaselineItems][ACTION] handleSetPageM1", { newPage: newPage });
		//To handle page change from Pagination (MetreaUI)
		setPageM1(newPage);
	};

	const handleSetRowsPerPage = (option) => {
		debugLog(3, "[BaselineItems][ACTION] handleSetRowsPerPage", { option: option });
		setRowsPerPage(option);
		setPageM1(1);
	};

	const handleSetOrder = (option) => {
		debugLog(3, "[BaselineItems][ACTION] handleSetOrder", { option: option });
		setOrder(option);
	};

	const handleSetOrderBy = (option) => {
		debugLog(3, "[BaselineItems][ACTION] handleSetOrderBy", { option: option });
		setOrderBy(option);
	};

	const handleTogglePreviewRow = (previewRowId) => {
		const normalized = String(previewRowId || "").trim();
		if (!normalized) return;
		const previewItems = Array.isArray(ingestPreviewReport?.previewItems) ? ingestPreviewReport.previewItems : [];
		const targetRow = previewItems.find((row) => String(row?.previewRowId || "").trim() === normalized);
		if (targetRow && !isPreviewRowImportAllowed(targetRow)) {
			return;
		}

		setSelectedPreviewRowIds((prev) => {
			const current = new Set(Array.isArray(prev) ? prev.map((value) => String(value)) : []);
			if (current.has(normalized)) {
				current.delete(normalized);
			} else {
				current.add(normalized);
			}
			return Array.from(current);
		});
	};

	const handleSetAllPreviewRowsSelected = (shouldSelect) => {
		if (!shouldSelect) {
			setSelectedPreviewRowIds([]);
			return;
		}

		const previewItems = Array.isArray(ingestPreviewReport?.previewItems) ? ingestPreviewReport.previewItems : [];
		setSelectedPreviewRowIds(
			previewItems
				.filter((row) => isPreviewRowImportAllowed(row))
				.map((row) => row?.previewRowId)
				.filter((value) => typeof value === "string" && value.trim())
		);
	};

	const handleDownloadGeneratedJson = async (previewReport, parseJob) => {
		const baselineId = previewReport?.baselineId;
		const jobId = parseJob?.jobId;
		if (!baselineId || !jobId) {
			showDialog({
				title: "Download Unavailable",
				type: "error",
				message: ["This file is not ready to download yet."],
			});
			return;
		}

		let objectUrl = null;
		try {
			const response = await BaselineService.downloadItemIngestParseJobResult(baselineId, jobId);
			const fileBlob = response?.data;
			if (!(fileBlob instanceof Blob) || fileBlob.size === 0) {
				throw new Error("Generated JSON download returned no file data.");
			}

			objectUrl = window.URL.createObjectURL(fileBlob);
			const link = document.createElement("a");
			link.href = objectUrl;
			link.download = parseJob.outputFileName || `${jobId}.generated.json`;
			link.rel = "noopener noreferrer";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			showDialog({
				title: "Download Started",
				type: "info",
				message: [
					"Your JSON file is downloading now.",
					"If nothing appears, check your browser downloads list.",
				],
			});
		} catch (error) {
			let failureMessage = "Could not download the JSON file.";
			const errorBlob = error?.response?.data;

			if (errorBlob instanceof Blob) {
				try {
					const rawText = await errorBlob.text();
					const trimmedText = String(rawText || "").trim();
					if (trimmedText) {
						try {
							const parsed = JSON.parse(trimmedText);
							failureMessage = parsed?.message || failureMessage;
						} catch (_parseError) {
							failureMessage = trimmedText;
						}
					}
				} catch (_blobReadError) {
					// Keep the fallback message when the error body cannot be decoded.
				}
			} else if (typeof error?.response?.data?.message === "string" && error.response.data.message.trim()) {
				failureMessage = error.response.data.message.trim();
			} else if (typeof error?.message === "string" && error.message.trim()) {
				failureMessage = error.message.trim();
			}

			showDialog({
				title: "Download Failed",
				type: "error",
				message: [failureMessage],
			});
		} finally {
			if (objectUrl) {
				window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
			}
		}
	};

	const handleImportSelectedPreviewRows = () => {
		if (!currBaseline?.id) {
			withError("Select or create a Baseline before importing items.", null, {
				title: "Import Error",
			});
			return;
		}

		const items = buildSelectedImportItems(ingestPreviewReport, selectedPreviewRowIds);
		if (items.length === 0) {
			withError("Select at least one review row before importing.", null, {
				title: "Import Error",
			});
			return;
		}

		const requestPayload = {
			items,
			reportMode: ingestPreviewReport?.importPayloadCandidate?.reportMode || "default",
		};

		withConfirmation(
			{
				title: "Import Selected Items",
				message: [
					`Import ${items.length} selected item(s) into Baseline #${currBaseline.id}?`,
					"Only selected review rows will be imported.",
				],
				confirmLabel: "Import Selected",
				cancelLabel: "Cancel",
			},
			() => {
				setIsImportingItems(true);
				BaselineService.importItems(currBaseline.id, requestPayload)
					.then((response) => {
						const report = response?.data ?? null;
						setImportReport(report);
						setIngestPreviewReport(null);
						setSelectedPreviewRowIds([]);
						showDialog({
							title: "Import Completed",
							type: "success",
							message: [
								`Imported: ${report?.insertedCount ?? 0}`,
								`Not imported: ${report?.rejectedCount ?? 0}`,
								"See the report panel below for per-item details.",
							],
						});

						if (props.onReloadBaseline) {
							props.onReloadBaseline(currBaseline.id);
						}
					})
					.catch((error) => {
						const status = error?.response?.status;
						const baseUrl = error?.config?.baseURL || "";
						const requestUrl = error?.config?.url || "";
						const endpoint = `${baseUrl}${requestUrl}` || "(unknown endpoint)";
						if (!status) {
							showDialog({
								title: "Import Failed",
								type: "error",
								message: [
									"Could not connect to the server to import items.",
									...(showTechnicalDetails
										? [
											`Endpoint: ${endpoint}`,
											`Error: ${error?.message || "Network error"}`,
										]
										: []),
									"Please check your connection and try again.",
								],
							});
							return;
						}
						withError(error, null, {
							title: "Import Failed",
							fallbackMessage: "Failed to import selected Baseline items.",
						});
					})
					.finally(() => {
						setIsImportingItems(false);
					});
			}
		);
	};

	const handleImportAllReviewedRows = () => {
		const importableRows = getImportablePreviewRows(ingestPreviewReport);
		const blockedCount = Math.max(
			0,
			(Array.isArray(ingestPreviewReport?.previewItems) ? ingestPreviewReport.previewItems.length : 0) -
				importableRows.length
		);
		const allRowIds = importableRows
			.map((row) => row?.previewRowId)
			.filter((value) => typeof value === "string" && value.trim());

		if (allRowIds.length === 0) {
			withError("No reviewed rows are available to import.", null, {
				title: "Import Error",
			});
			return;
		}

		if (!currBaseline?.id) {
			withError("Select or create a Baseline before importing items.", null, {
				title: "Import Error",
			});
			return;
		}

		const items = buildSelectedImportItems(ingestPreviewReport, allRowIds);
		if (items.length === 0) {
			withError("No reviewed candidate rows were available for import.", null, {
				title: "Import Error",
			});
			return;
		}

		const requestPayload = {
			items,
			reportMode: ingestPreviewReport?.importPayloadCandidate?.reportMode || "default",
		};

		withConfirmation(
			{
				title: "Import Reviewed Items",
				message: [
					`Import ${items.length} reviewed item(s) into Baseline #${currBaseline.id}?`,
					blockedCount > 0
						? `${blockedCount} review row(s) are excluded because they are blocked by scaffold or review-state guardrails.`
						: "This mode imports all reviewed rows together.",
				],
				confirmLabel: "Import Reviewed Rows",
				cancelLabel: "Cancel",
			},
			() => {
				setIsImportingItems(true);
				BaselineService.importItems(currBaseline.id, requestPayload)
					.then((response) => {
						const report = response?.data ?? null;
						setImportReport(report);
						setIngestPreviewReport(null);
						setSelectedPreviewRowIds([]);
						showDialog({
							title: "Import Completed",
							type: "success",
							message: [
								`Imported: ${report?.insertedCount ?? 0}`,
								`Not imported: ${report?.rejectedCount ?? 0}`,
								"See the report panel below for per-item details.",
							],
						});

						if (props.onReloadBaseline) {
							props.onReloadBaseline(currBaseline.id);
						}
					})
					.catch((error) => {
						const status = error?.response?.status;
						const baseUrl = error?.config?.baseURL || "";
						const requestUrl = error?.config?.url || "";
						const endpoint = `${baseUrl}${requestUrl}` || "(unknown endpoint)";
						if (!status) {
							showDialog({
								title: "Import Failed",
								type: "error",
								message: [
									"Could not connect to the server to import items.",
									...(showTechnicalDetails
										? [
											`Endpoint: ${endpoint}`,
											`Error: ${error?.message || "Network error"}`,
										]
										: []),
									"Please check your connection and try again.",
								],
							});
							return;
						}
						withError(error, null, {
							title: "Import Failed",
							fallbackMessage: "Failed to import reviewed Baseline items.",
						});
					})
					.finally(() => {
						setIsImportingItems(false);
					});
			}
		);
	};

	const handleImportItemsUpload = (loadedFile) => {
		if (!loadedFile) return;
		if (typeof loadedFile !== "object") {
			withError("Invalid import file. Please upload a JSON object payload.", null, {
				title: "Import Error",
			});
			return;
		}

		if (loadedFile.invalidType) {
			withError("Unsupported file type. Please upload JSON. PDF ingest review is coming soon.", null, {
				title: "Import Error",
			});
			return;
		}

		const fileSizeBytes =
			Number.isFinite(loadedFile?.fileSizeBytes) && loadedFile.fileSizeBytes >= 0
				? loadedFile.fileSizeBytes
				: null;

		if (loadedFile.fileType === "pdf" && !ingestRolloutCapabilities.canUploadPdf) {
			withError(BASELINE_ITEM_JSON_ONLY_COPY, null, {
				title: "PDF Ingest Review",
			});
			return;
		}

		if (loadedFile.sizeLimitExceeded) {
			if (loadedFile.fileType === "pdf") {
				withError(
					`PDF ingest review files must be ${formatMegabyteLimitLabel(BASELINE_ITEM_REVIEW_PDF_MAX_BYTES)} or smaller.`,
					null,
					{
						title: "PDF Ingest Review",
					}
				);
				return;
			}

			if (loadedFile.fileType === "json") {
				withError(
					`Baseline Item Import JSON files must be ${formatMegabyteLimitLabel(BASELINE_ITEM_IMPORT_JSON_MAX_BYTES)} or smaller.`,
					null,
					{
						title: "Import Error",
					}
				);
				return;
			}
		}

		if (loadedFile.fileType === "pdf") {
			if (fileSizeBytes != null && fileSizeBytes > BASELINE_ITEM_REVIEW_PDF_MAX_BYTES) {
				withError(
					`PDF ingest review files must be ${formatMegabyteLimitLabel(BASELINE_ITEM_REVIEW_PDF_MAX_BYTES)} or smaller.`,
					null,
					{
						title: "PDF Ingest Review",
					}
				);
				return;
			}

			if (!currBaseline?.id) {
				withError("Select or create a Baseline before starting PDF ingest review.", null, {
					title: "PDF Ingest Review",
				});
				return;
			}

			if (!Array.isArray(itemIngestProfiles) || itemIngestProfiles.length === 0) {
				withError("PDF ingest profiles are unavailable. Reload and try again.", null, {
					title: "PDF Ingest Review",
				});
				return;
			}

			setUseMappingFallbackForReview(false);
			setPendingPdfUpload(loadedFile);
			setPendingPdfProfileId("");
			setIsPdfProfileModalOpen(true);
			return;
		}

		if (loadedFile.parseError) {
			withError("Invalid JSON file. Please verify the JSON content and try again.", null, {
				title: "Import Error",
			});
			return;
		}

		if (fileSizeBytes != null && fileSizeBytes > BASELINE_ITEM_IMPORT_JSON_MAX_BYTES) {
			withError(
				`Baseline Item Import JSON files must be ${formatMegabyteLimitLabel(BASELINE_ITEM_IMPORT_JSON_MAX_BYTES)} or smaller.`,
				null,
				{
					title: "Import Error",
				}
			);
			return;
		}

		if (!currBaseline?.id) {
			withError("Select or create a Baseline before importing items.", null, {
				title: "Import Error",
			});
			return;
		}

		const { items, wasConvertedFromGeneratedJson } = extractImportItemsFromUploadPayload(loadedFile);
		if (!items) {
			withError("Import JSON must contain an `items` array.", null, {
				title: "Import Error",
			});
			return;
		}

		if (wasConvertedFromGeneratedJson) {
			if (ingestRolloutCapabilities.canReviewGeneratedJson) {
				setUseMappingFallbackForReview(false);
				setPendingGeneratedJsonUpload(loadedFile);
				setPendingGeneratedJsonProfileId("");
				setIsGeneratedJsonProfileModalOpen(true);
				return;
			}

			// Outside the staged rollout, preserve the older direct-import fallback
			// so generated JSON still behaves like the existing JSON upload path.
			showDialog({
				title: "Generated JSON Detected",
				type: "info",
				message: [
					`Detected JSON line items and converted ${items.length} item(s) for import.`,
					"Direct import is available in this mode.",
				],
			});
		}

		const requestPayload = {
			items,
			reportMode: loadedFile.reportMode || "default",
		};

		withConfirmation(
			{
				title: "Import Items",
				message: [
					`Import ${items.length} item(s) into Baseline #${currBaseline.id}?`,
					"Items that cannot be imported will be listed in the report.",
				],
				confirmLabel: "Import",
				cancelLabel: "Cancel",
			},
			() => {
				setIsImportingItems(true);
				BaselineService.importItems(currBaseline.id, requestPayload)
					.then((response) => {
						const report = response?.data ?? null;
						setImportReport(report);
						showDialog({
							title: "Import Completed",
							type: "success",
							message: [
								`Imported: ${report?.insertedCount ?? 0}`,
								`Not imported: ${report?.rejectedCount ?? 0}`,
								"See the report panel below for per-item details.",
							],
						});

						if (props.onReloadBaseline) {
							props.onReloadBaseline(currBaseline.id);
						}
					})
					.catch((error) => {
						const status = error?.response?.status;
						const baseUrl = error?.config?.baseURL || "";
						const requestUrl = error?.config?.url || "";
						const endpoint = `${baseUrl}${requestUrl}` || "(unknown endpoint)";
						if (!status) {
							showDialog({
								title: "Import Failed",
								type: "error",
								message: [
									"Could not connect to the server to import items.",
									...(showTechnicalDetails
										? [
											`Endpoint: ${endpoint}`,
											`Error: ${error?.message || "Network error"}`,
										]
										: []),
									"Please check your connection and try again.",
								],
							});
							return;
						}
						withError(error, null, {
							title: "Import Failed",
							fallbackMessage: "Failed to import Baseline items.",
						});
					})
					.finally(() => {
						setIsImportingItems(false);
					});
			}
		);
	};

	const closePdfProfileModal = () => {
		if (isPreparingIngestPreview) return;
		resetPdfProfileModalState();
	};

	const resetPdfProfileModalState = () => {
		setIsPdfProfileModalOpen(false);
		setPendingPdfUpload(null);
		setPendingPdfProfileId("");
		setUseMappingFallbackForReview(false);
	};

	const closeGeneratedJsonProfileModal = () => {
		if (isPreparingIngestPreview) return;
		setIsGeneratedJsonProfileModalOpen(false);
		setPendingGeneratedJsonUpload(null);
		setPendingGeneratedJsonProfileId("");
		setUseMappingFallbackForReview(false);
	};

	const handleConfirmPdfIngestReview = () => {
		if (!pendingPdfUpload?.file) {
			withError("Select a PDF file before starting ingest review.", null, {
				title: "PDF Ingest Review",
			});
			return;
		}

		if (!pendingPdfProfileId) {
			withError("Select an ingest profile to continue.", null, {
				title: "PDF Ingest Review",
			});
			return;
		}

		if (!currBaseline?.id) {
			withError("Select or create a Baseline before starting PDF ingest review.", null, {
				title: "PDF Ingest Review",
			});
			return;
		}

		setIsPreparingIngestPreview(true);
		BaselineService.reviewItemIngest(currBaseline.id, pendingPdfUpload.file, pendingPdfProfileId, {
			reportMode: "default",
			useMappingFallback: effectiveUseMappingFallbackForReview,
		})
				.then((response) => {
					const preview = response?.data ?? null;
					setIngestPreviewReport(preview);
					resetPdfProfileModalState();
					const isImmediateScaffold = Boolean(preview?.ingestRun?.scaffoldMode);
					showDialog({
						title: isImmediateScaffold ? "PDF Scaffold Loaded" : "PDF Review Started",
						type: "info",
						message: isImmediateScaffold
							? [
								`Selected file: ${preview?.ingestRun?.filename || pendingPdfUpload?.filename || "(unknown)"}`,
								`Profile: ${getSourceProfileDisplayLabel(preview?.sourceProfile) || pendingPdfProfileId}`,
								preview?.intakeReadiness?.summary ||
									"RAVEN kept this upload in scaffold fallback because it is not review-ready yet.",
								"Investigate the PDF/parser result and extend the route only after the data-repo proof and Raven follow-through are ready.",
							]
							: [
								`Selected file: ${preview?.ingestRun?.filename || pendingPdfUpload?.filename || "(unknown)"}`,
								`Profile: ${getSourceProfileDisplayLabel(preview?.sourceProfile) || pendingPdfProfileId}`,
								...(showTechnicalDetails
									? [
										`Compatibility: ${preview?.compatibility?.classification || "unknown"}`,
										`Processing status: ${preview?.parseJob?.status || "queued"}`,
									]
									: []),
								"RAVEN is processing the PDF now. This screen will update automatically when the review results are ready.",
							],
					});
				})
				.catch((error) => {
					// Startup failed before any parse job existed, so fully tear down
					// the PDF profile flow instead of leaving the underlying modal open.
					resetPdfProfileModalState();
					withError(error, null, {
						title: "PDF Ingest Review Failed",
						fallbackMessage: "Failed to start the PDF ingest review flow.",
					});
				})
				.finally(() => {
					setIsPreparingIngestPreview(false);
				});
	};

	const handleConfirmGeneratedJsonIngestReview = () => {
		if (!pendingGeneratedJsonUpload) {
			withError("Select a JSON file before starting review.", null, {
				title: "Generated JSON Review",
			});
			return;
		}

		if (!pendingGeneratedJsonProfileId) {
			withError("Select an ingest profile to continue.", null, {
				title: "Generated JSON Review",
			});
			return;
		}

		if (!currBaseline?.id) {
			withError("Select or create a Baseline before starting generated JSON review.", null, {
				title: "Generated JSON Review",
			});
			return;
		}

		setIsPreparingIngestPreview(true);
		BaselineService.reviewItemIngestGeneratedJson(currBaseline.id, pendingGeneratedJsonUpload, {
			filename: pendingGeneratedJsonUpload.filename,
			sourceProfileId: pendingGeneratedJsonProfileId,
			reportMode: pendingGeneratedJsonUpload.reportMode || "default",
			useMappingFallback: effectiveUseMappingFallbackForReview,
		})
			.then((response) => {
				const preview = response?.data ?? null;
				setIngestPreviewReport(preview);
				setIsGeneratedJsonProfileModalOpen(false);
				setPendingGeneratedJsonUpload(null);
				setPendingGeneratedJsonProfileId("");
				setUseMappingFallbackForReview(false);
				showDialog({
					title: preview?.ingestRun?.scaffoldMode ? "Generated JSON Scaffold Loaded" : "Generated JSON Review Ready",
					type: preview?.ingestRun?.scaffoldMode ? "warning" : "info",
					message: preview?.ingestRun?.scaffoldMode
						? [
							"RAVEN accepted the uploaded JSON file but kept it in scaffold fallback.",
							`Readiness: ${preview?.intakeReadiness?.summary || preview?.intakeReadiness?.status || "Authoritative Stage 2 metadata is still required."}`,
							"Full Stage 3 review rows unlock when authoritative Stage 2 route/readiness metadata is preserved or supplied with the payload.",
						]
						: [
							`Loaded ${preview?.summary?.candidateItems ?? 0} review row(s) from the uploaded JSON file.`,
							`Profile: ${getSourceProfileDisplayLabel(preview?.sourceProfile) || pendingGeneratedJsonProfileId}`,
							ingestRolloutCapabilities.canSelectReviewedRows
								? "Review rows are now loaded. Select the rows you want before importing."
								: ingestRolloutCapabilities.canImportReviewedRows
									? "Review rows are now loaded. You can import all reviewed rows together in this view."
									: "Review rows are now loaded in read-only mode. Import and row selection are not available here.",
						],
				});
			})
			.catch((error) => {
				withError(error, null, {
					title: "Generated JSON Review Failed",
					fallbackMessage: "Failed to load generated JSON into the ingest review flow.",
				});
			})
			.finally(() => {
				setIsPreparingIngestPreview(false);
			});
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[BaselineItems][FLOW] render", {
		renderCount: renderCount.current,
		currBaseline: currBaseline,
		currentItem: currentItem,
	});
	debugLog(9, "[BaselineItems][FLOW] renderState", {
		props: props,
		currBaseline: currBaseline,
		currentItem: currentItem,
		order: order,
		orderBy: orderBy,
		pageM1: pageM1,
		rowsPerPage: rowsPerPage,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<div data-testid="BaselineItems">
			{isPdfProfileModalOpen && (
				<AppModal
					open={isPdfProfileModalOpen}
					onClose={closePdfProfileModal}
					style={{ maxWidth: "560px", width: "92vw" }}
				>
					<Box p={4} display="grid" gap="12px">
						<div style={{ fontWeight: 600, fontSize: "1rem" }}>Confirm PDF Ingest Profile</div>
						<div style={{ fontSize: "0.9rem" }}>
							<div>File: {pendingPdfUpload?.filename || "(unknown)"}</div>
							<div>
								Select an ingest profile to continue.
							</div>
							<div>
								Mapping assistance:{" "}
								{ingestRolloutCapabilities.canUseMappingAssistance
									? effectiveUseMappingFallbackForReview
										? "enabled"
										: "available"
									: "locked in this rollout phase"}
							</div>
							<div>
								{ingestRolloutCapabilities.canSelectReviewedRows
									? "Review selection will be available after this preview starts."
									: ingestRolloutCapabilities.canImportReviewedRows
										? "This preview will support whole-row import without row selection."
										: "This preview will open in read-only mode."}
							</div>
						</div>

						<MetreaSelectRoot
							variant="outline"
							collection={ingestProfileCollection}
							size={sizeToSelectSize(uiSize)}
							name="pdf_source_profile"
							value={[String(pendingPdfProfileId || "")]}
							onValueChange={(details) => {
								const nextValue = Array.isArray(details?.value)
									? String(details.value[0] ?? "")
									: "";
								setPendingPdfProfileId(nextValue);
							}}
						>
							<MetreaSelectTrigger id="baseline-items-pdf-source-profile">
								<MetreaSelectValueText placeholder="Select an ingest profile..." />
							</MetreaSelectTrigger>
							<Portal>
								<MetreaSelectContent>
									{ingestProfileCollection.items.map((item) => (
										<MetreaSelectItem key={item.value || "empty"} item={item}>
											{item.label}
										</MetreaSelectItem>
									))}
								</MetreaSelectContent>
							</Portal>
						</MetreaSelectRoot>

						{ingestRolloutCapabilities.canUseMappingAssistance ? (
							<Checkbox.Root
								checked={useMappingFallbackForReview}
								onCheckedChange={(next) => {
									const checked =
										next === true ||
										next === "indeterminate" ||
										next?.checked === true ||
										next?.detail?.checked === true;
									setUseMappingFallbackForReview(checked);
								}}
								style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
							>
								<Checkbox.HiddenInput />
								<Checkbox.Control />
								<div style={{ fontSize: "0.9rem" }}>Use mapping assistance</div>
							</Checkbox.Root>
						) : (
							<div style={{ fontSize: "0.85rem", color: "rgba(0, 0, 0, 0.65)" }}>
								Mapping assistance is locked in this rollout phase.
							</div>
						)}

						<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
							<Button variant="outline" onClick={closePdfProfileModal} disabled={isPreparingIngestPreview}>
								Cancel
							</Button>
							<Button
								onClick={handleConfirmPdfIngestReview}
								disabled={!pendingPdfProfileId || isPreparingIngestPreview}
							>
								{isPreparingIngestPreview ? "Preparing..." : "Start Preview"}
							</Button>
						</div>
					</Box>
				</AppModal>
			)}
			{isGeneratedJsonProfileModalOpen && (
				<AppModal
					open={isGeneratedJsonProfileModalOpen}
					onClose={closeGeneratedJsonProfileModal}
					style={{ maxWidth: "560px", width: "92vw" }}
				>
					<Box p={4} display="grid" gap="12px">
						<div style={{ fontWeight: 600, fontSize: "1rem" }}>Confirm Generated JSON Ingest Profile</div>
						<div style={{ fontSize: "0.9rem" }}>
							<div>File: {pendingGeneratedJsonUpload?.filename || "(unknown)"}</div>
							<div>
								Select an ingest profile to continue.
							</div>
							<div>
								Mapping assistance:{" "}
								{ingestRolloutCapabilities.canUseMappingAssistance
									? effectiveUseMappingFallbackForReview
										? "enabled"
										: "available"
									: "locked in this rollout phase"}
							</div>
							<div>
								{ingestRolloutCapabilities.canSelectReviewedRows
									? "Review selection will be available after this preview starts."
									: ingestRolloutCapabilities.canImportReviewedRows
										? "This preview will support whole-row import without row selection."
										: "This preview will open in read-only mode."}
							</div>
						</div>

						<MetreaSelectRoot
							variant="outline"
							collection={ingestProfileCollection}
							size={sizeToSelectSize(uiSize)}
							name="generated_json_source_profile"
							value={[String(pendingGeneratedJsonProfileId || "")]}
							onValueChange={(details) => {
								const nextValue = Array.isArray(details?.value)
									? String(details.value[0] ?? "")
									: "";
								setPendingGeneratedJsonProfileId(nextValue);
							}}
						>
							<MetreaSelectTrigger id="baseline-items-generated-json-source-profile">
								<MetreaSelectValueText placeholder="Select an ingest profile..." />
							</MetreaSelectTrigger>
							<Portal>
								<MetreaSelectContent>
									{ingestProfileCollection.items.map((item) => (
										<MetreaSelectItem key={item.value || "empty"} item={item}>
											{item.label}
										</MetreaSelectItem>
									))}
								</MetreaSelectContent>
							</Portal>
						</MetreaSelectRoot>

						{ingestRolloutCapabilities.canUseMappingAssistance ? (
							<Checkbox.Root
								checked={useMappingFallbackForReview}
								onCheckedChange={(next) => {
									const checked =
										next === true ||
										next === "indeterminate" ||
										next?.checked === true ||
										next?.detail?.checked === true;
									setUseMappingFallbackForReview(checked);
								}}
								style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
							>
								<Checkbox.HiddenInput />
								<Checkbox.Control />
								<div style={{ fontSize: "0.9rem" }}>Use mapping assistance</div>
							</Checkbox.Root>
						) : (
							<div style={{ fontSize: "0.85rem", color: "rgba(0, 0, 0, 0.65)" }}>
								Mapping assistance is locked in this rollout phase.
							</div>
						)}

						<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
							<Button
								variant="outline"
								onClick={closeGeneratedJsonProfileModal}
								disabled={isPreparingIngestPreview}
							>
								Cancel
							</Button>
							<Button
								onClick={handleConfirmGeneratedJsonIngestReview}
								disabled={!pendingGeneratedJsonProfileId || isPreparingIngestPreview}
							>
								{isPreparingIngestPreview ? "Preparing..." : "Start Review"}
							</Button>
						</div>
					</Box>
				</AppModal>
			)}

			{!currentItem && (
				<>
					{pageMode === "edit" && ingestRolloutCapabilities.canUploadPdf && (
						<Box mb={3}>
							<div style={ingestRuntimeStatusBoxStyle}>
								<div style={{ fontWeight: 600 }}>{getPdfRuntimeStatusLabel(itemIngestPdfRuntime)}</div>
								<div>
									{itemIngestPdfRuntime?.provider?.summary ||
										"PDF runtime status is unavailable in this environment."}
								</div>
								{getPdfRuntimeModeLabel(itemIngestPdfRuntime) && (
									<div>Configured mode: {getPdfRuntimeModeLabel(itemIngestPdfRuntime)}</div>
								)}
								{getPdfRuntimeModeSummary(itemIngestPdfRuntime) && (
									<div>{getPdfRuntimeModeSummary(itemIngestPdfRuntime)}</div>
								)}
								{getPdfRuntimeConfigHint(itemIngestPdfRuntime) && (
									<div>{getPdfRuntimeConfigHint(itemIngestPdfRuntime)}</div>
								)}
								<div>Proven PDF lanes: {formatPdfRuntimeSupportedLanes(itemIngestPdfRuntime)}</div>
							</div>
						</Box>
					)}
					{ingestRolloutCapabilities.canReviewGeneratedJson &&
						renderIngestPreviewReport(
							ingestPreviewReport,
							selectedPreviewRowIds,
							handleTogglePreviewRow,
							handleSetAllPreviewRowsSelected,
							() => {
								setIngestPreviewReport(null);
								setSelectedPreviewRowIds([]);
								setReviewPageM1(1);
							},
							handleImportSelectedPreviewRows,
							handleImportAllReviewedRows,
							isImportingItems,
							ingestRolloutCapabilities,
							handleDownloadGeneratedJson,
							reviewPageM1,
							reviewRowsPerPage,
							setReviewPageM1,
							(nextValue) => setReviewRowsPerPage(Number(nextValue) || 10),
							reviewRowsPerPageCollection,
							showTechnicalDetails
						)}
					{renderImportReport(importReport, () => setImportReport(null))}

					<BaselineItemList
						mode={pageMode}
						order={order}
						orderBy={orderBy}
						pageM1={pageM1}
						onSetPageM1={handleSetPageM1}
						rowsPerPage={rowsPerPage}
						onSetRowsPerPage={handleSetRowsPerPage}
						onSetOrder={handleSetOrder}
						onSetOrderBy={handleSetOrderBy}
						onSelectBaselineItem={handleSelectBaselineItem}
						onChangeStatus={handleChangeStatusFromList}
						onSoftDelete={handleSoftDeleteFromList}
						onPurge={handlePurgeFromList}
						onAddItem={handleAddItem}
						addItemAdjacentSlot={
							pageMode === "edit" ? (
								<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
									<InputFileUpload
										label={ingestRolloutCapabilities.canUploadPdf ? "Upload" : "Upload JSON"}
										size="2xs"
										variant="subtle"
										buttonStyle={uploadButtonStyle}
										toolTip={ingestRolloutCapabilities.canUploadPdf ? "Upload importer payload JSON or start PDF ingest review" : BASELINE_ITEM_JSON_ONLY_COPY}
										accept={ingestRolloutCapabilities.canUploadPdf ? ".json,.pdf" : ".json"}
										maxJsonFileSizeBytes={BASELINE_ITEM_IMPORT_JSON_MAX_BYTES}
										maxPdfFileSizeBytes={BASELINE_ITEM_REVIEW_PDF_MAX_BYTES}
										onLoad={handleImportItemsUpload}
									/>
									{isImportingItems && <span style={{ fontSize: "0.85rem" }}>Importing...</span>}
									{isPreparingIngestPreview && (
										<span style={{ fontSize: "0.85rem" }}>Preparing PDF review...</span>
									)}
								</div>
							) : null
						}
					/>
				</>
			)}
			{currentItem && (
				<>
					<Box w="100%">
						<BaselineItemConfigs
							formFieldWidth={"100%"}
							item={currentItem}
							mode={pageMode}
							size={uiSize || "small"}
							onLocalItemChange={handleLocalItemChange}
							onClearCurrentItem={handleClearCurrentItem}
							onReloadCurrentItem={handleSelectBaselineItem}
							onSaveDataToDB={handleSaveBaseline}
							onClearComplete={handleClearComplete}
							onReloadBaseline={handleReloadBaseline}
							onSetDataChanged={props.onSetDataChanged}
						/>
					</Box>
				</>
			)}
		</div>
	);
};

export default BaselineItems;
